import { FormControl, Typography } from '@mui/material';
import {
  type ExposureOrGroup,
  useCreateQuoteFromSubmission,
  useUpdatePolicyData,
} from 'api/submission';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { ExposuresList, ExposuresListItem, ExposuresWrapper } from 'components/ExposuresWrapper';
import { FormCheckbox } from 'components/lib/FormCheckbox';
import { useFormik } from 'formik';
import { formatDate } from 'helpers/utils';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { useScrollToFirstErroredInput } from 'hooks/useScrollToFirstErroredInput';
import { DateTime } from 'luxon';
import { type FC, useEffect, useRef } from 'react';
import { useFullscreenLoader } from 'store/features/UI';
import * as yup from 'yup';
import type { IClaim } from './Claims.types';
import { ClaimModal } from './components/Modal';
import type { ClaimModalRef } from './components/Modal/ClaimModal.types';

const category = 'claims';

const title = (data: IClaim) => `${data.pol_claims_type}`;

const Claims: FC = () => {
  const { setFullscreenLoading } = useFullscreenLoader();

  const { relatedResource, performAction, submissionId, submission } =
    useExposuresOrGroupFields<IClaim>({
      type: 'group',
      name: 'pol_claims',
    });

  const { mutateAsync: updatePolicyData } = useUpdatePolicyData(submissionId);

  const { mutateAsync: createQuoteFromSubmission } = useCreateQuoteFromSubmission(submissionId);

  const claimModalRef = useRef<ClaimModalRef>(undefined);

  const handleAdd = async (data: IClaim) =>
    performAction({
      action: 'add',
      data,
      loadingText: `Adding ${title(data)}`,
    });

  const handleUpdate = async (data: IClaim, id: string) =>
    performAction({
      action: 'update',
      data,
      resourceId: id,
      loadingText: `Updating the ${title(data)}`,
    });

  const handleDelete = async (claim: ExposureOrGroup<IClaim>) =>
    performAction({
      action: 'delete',
      resourceId: claim.id,
      loadingText: `Deleting the ${title(claim.data)}`,
    });

  const formik = useFormik({
    initialValues: {
      noRecentClaimsConfirmed:
        (submission?.data?.policy_data?.noRecentClaimsConfirmed as boolean) || false,
      hasRecentClaims: false,
    },
    validationSchema: yup.object({
      noRecentClaimsConfirmed: yup.boolean().when('hasRecentClaims', {
        is: false,
        then: (schema) =>
          schema.oneOf(
            [true],
            'Before proceeding, please confirm that no claims occurred, including claims that did not result in a payout, or add any claims below.',
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useScrollToFirstErroredInput({ formik });

  useEffect(() => {
    const threeYearsAgo = DateTime.now().startOf('day').minus({ years: 3 }).toJSDate();
    const hasRecentClaims = relatedResource.some((claim) => {
      const rawDate = claim.data.pol_claims_date;

      if (!rawDate) return false;

      const claimDate =
        typeof rawDate === 'string' ? DateTime.fromISO(rawDate) : DateTime.fromJSDate(rawDate);

      return claimDate.isValid && claimDate.startOf('day').toJSDate() >= threeYearsAgo;
    });

    formik.setFieldValue('hasRecentClaims', hasRecentClaims, true);
  }, [relatedResource]);

  useEffect(() => {
    if (formik.values.hasRecentClaims) {
      formik.setFieldValue('noRecentClaimsConfirmed', false, true);
      formik.setTouched({ noRecentClaimsConfirmed: false });
    }
  }, [formik.values.hasRecentClaims]);

  const handleCreateQuote = async () => {
    await formik.validateForm();
    formik.setTouched({ noRecentClaimsConfirmed: true });

    if (!formik.isValid) {
      return false;
    }

    try {
      setFullscreenLoading(true, 'Creating quote');

      await updatePolicyData({
        id: submissionId,
        body: {
          ...submission?.data?.policy_data,
          noRecentClaimsConfirmed: formik.values.noRecentClaimsConfirmed,
        },
      });

      await createQuoteFromSubmission(submissionId);
      return false;
    } catch (err) {
      displayBackendErrorMessage(err);
      return false;
    } finally {
      setFullscreenLoading(false);
    }
  };

  return (
    <>
      <ClaimModal
        ref={claimModalRef}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        category={category}
      />

      <ExposuresWrapper
        title="3-year claims history"
        description="List all claims filed within the last 3 years, including claims that did not result in a payout."
        addButtonText={`Add ${category}`}
        onAddBtnClick={() => claimModalRef.current?.openModal()}
        onNext={handleCreateQuote}
      >
        {!formik.values.hasRecentClaims && (
          <FormControl>
            <FormCheckbox
              id="noRecentClaimsConfirmed"
              name="noRecentClaimsConfirmed"
              data-test="noRecentClaimsConfirmed"
              labelText="I confirm that no claims occurred within the last 3 years."
              checked={formik.values.noRecentClaimsConfirmed}
              onChange={formik.handleChange}
            />
            {formik.touched.noRecentClaimsConfirmed && formik.errors.noRecentClaimsConfirmed && (
              <Typography color="error">{formik.errors.noRecentClaimsConfirmed}</Typography>
            )}
          </FormControl>
        )}

        <ExposuresList>
          {relatedResource.map((claim) => (
            <ExposuresListItem
              key={claim.id}
              title={claim.data.pol_claims_type}
              category={category}
              onEdit={() => claimModalRef.current?.openModal(claim)}
              onDelete={() => {
                handleDelete(claim);
              }}
            >
              <Typography>{formatDate(claim.data.pol_claims_date)}</Typography>
              <Typography>{claim.data.pol_claims_amount}</Typography>
            </ExposuresListItem>
          ))}
        </ExposuresList>
      </ExposuresWrapper>
    </>
  );
};

export default Claims;
